import styled from "styled-components";

interface StyledButtonProps {
  $disabled?: boolean;
  $variant?: "primary" | "secondary" | "outline";
  $size?: "small" | "medium" | "large";
}

const getButtonSizes = (size: "small" | "medium" | "large") => {
  switch (size) {
    case "small":
      return {
        padding: "2px 8px",
        fontSize: "12px",
        minHeight: "28px",
      };
    case "large":
      return {
        padding: "8px 20px",
        fontSize: "16px",
        minHeight: "44px",
      };
    default: // medium
      return {
        padding: "4px 14px",
        fontSize: "14px",
        minHeight: "36px",
      };
  }
};

const getButtonVariantStyles = (
  variant: "primary" | "secondary" | "outline"
) => {
  switch (variant) {
    case "secondary":
      return {
        backgroundColor: "#f5f5f5",
        color: "#333",
        border: "2px solid #ccc",
        hoverBg: "#e0e0e0",
        hoverColor: "#333",
      };
    case "outline":
      return {
        backgroundColor: "transparent",
        color: "black",
        border: "2px solid #ff8038",
        hoverBg: "#ff8038",
        hoverColor: "white",
      };
    default: // primary
      return {
        backgroundColor: "#ff8038",
        color: "white",
        border: "2px solid #ff8038",
        hoverBg: "#fd954f",
        hoverColor: "white",
      };
  }
};

export const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-weight: 500;
  text-transform: capitalize;
  border: none;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};

  ${(props) => {
    const { padding, fontSize, minHeight } = getButtonSizes(
      props.$size || "medium"
    );
    return `
      padding: ${padding};
      font-size: ${fontSize};
      min-height: ${minHeight};
    `;
  }}

  ${(props) => {
    const { backgroundColor, color, border } = getButtonVariantStyles(
      props.$variant || "primary"
    );
    return `
      background-color: ${props.$disabled ? "#f0f0f0" : backgroundColor};
      color: ${props.$disabled ? "#999" : color};
      border: ${border};
    `;
  }}

  &:hover {
    ${(props) => {
      if (props.$disabled) return "";
      const { hoverBg, hoverColor } = getButtonVariantStyles(
        props.$variant || "primary"
      );
      return `
        background-color: ${hoverBg};
        color: ${hoverColor};
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        
        .info-icon {
          color: ${hoverColor} !important;
        }
      `;
    }}
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 128, 56, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: #f0f0f0;
    color: #999;
    border-color: #ddd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
